import { useState, useMemo } from "react";
import { useNavigate } from "react-router-dom";
import { useAlertsQuery } from "./hooks/useAlertsQuery";
import { useChartsQuery } from "./hooks/useChartsQuery";
import { useCountDailyOperatingVehiclesQuery } from "../preoperative/hooks/useCountDailyOperatingVehiclesQuery";
import { useCountMaintenancesQuery } from "../maintenance/hooks/useCountMaintenancesQuery";
import { useDaysWithoutIncidentsQuery } from "./hooks/useDaysWithoutIncidentsQuery";
import { DateUtil } from "../../common/utils/date.util";
import { ROUTES } from "../../core/routes/routes";
import DoughnutChart from "../../common/components/Charts/DoughnutChart";
import CardAlert from "./components/CardAlert";
import Select from "../../common/components/Select";
import CardNumber from "../../common/components/CardNumber";
import Button from "../../common/components/Button"
import { Offcanvas } from "react-bootstrap";
import AsideContent from "./components/AsideContent";
import { ALERT_CATEGORY_FILTER_OPTIONS } from "./alertCategoryFilterOptions";

const BACKGROUND_PIE_COLORS = [
  "#393E46",
  "#1D24CA",
  "#8F1D14",
  "#a78bfa",
  "#60a5fa",
];

function Dashboard() {
  const navigate = useNavigate();
  const [showAlerts, setShowAlerts] = useState(false);
  const [filters, setFilters] = useState({ category: "" });
  const { alerts } = useAlertsQuery(filters);
  const { dailyReview, annualPlanCompliance, trainingPlanCompliance } =
    useChartsQuery();

  const dailyVehicleFilters = useMemo(
    () => ({ startDate: DateUtil.now(), endDate: DateUtil.now() }),
    []
  );
  const { countDailyOperatingVehicles } = useCountDailyOperatingVehiclesQuery(dailyVehicleFilters);

  const maintenanceCountFilters = useMemo(
    () => ({ startDate: DateUtil.getStartOfYear(), endDate: DateUtil.now() }),
    []
  );
  const { countMaintenances } = useCountMaintenancesQuery(maintenanceCountFilters);
  const { daysWithoutIncidents } = useDaysWithoutIncidentsQuery();

  const calculateCompliancePercentage = (count, total) => {
    if (total === 0) return 0;
    return ((count / total) * 100).toFixed(2);
  };

  const handleClick = (category) => {
    switch (category) {
      case "mantenimiento":
        navigate(ROUTES.LIST_MAINTENANCE);
        break;
      case "capacitacion_vial":
        navigate(ROUTES.WORK_PLAN_AND_TRAINING);
        break;
      case "capacitacion_plan_anual":
        navigate(ROUTES.WORK_PLAN_AND_TRAINING);
        break;
      case "documentos_por_vencer":
        // No navega a ningún lado: el usuario ya ve el detalle en la alerta.
        break;
      default:
        console.log("No action defined for this category");
    }
  };

  const handleShow = () => setShowAlerts(true);
  const handleClose = () => setShowAlerts(false);

  return (
    <main className="d-flex w-100 h-100 overflow-hidden">

      <div className="d-block d-lg-none p-2 position-absolute end-0">
        <Button variant="dark" onClick={handleShow}>
          Ver Alertas ({alerts.length})
        </Button>
      </div>

      <aside
        className="flex-shrink-0 p-4 d-none d-lg-block"
        style={{
          width: "340px",
          backgroundColor: "#f9f9f9",
          borderLeft: "1px solid #e0e0e0",
          height: "100%",
          overflowY: "auto",
          boxShadow: "-2px 0 4px rgba(0,0,0,0.05)",
          maxHeight: "calc(100vh - 3rem)",
          minHeight: "calc(100vh - 3rem)",
        }}
      >
        <div>
          <h2 style={{ fontSize: "1.25rem", marginBottom: "1rem" }}>
            Alertas
            <span className="badge bg-dark ms-2">{alerts.length}</span>
          </h2>
          <Select
            value={filters.category}
            onChange={({ value }) =>
              setFilters({ ...filters, category: value })
            }
            options={ALERT_CATEGORY_FILTER_OPTIONS}
          />
        </div>

        <div style={{ display: "flex", flexDirection: "column", gap: "1rem" }}>
          {alerts.map((alert) => (
            <CardAlert
              key={alert.id}
              title={alert.title}
              description={alert.description}
              date={DateUtil.format(alert.date)}
              category={alert.category}
              onClick={() => handleClick(alert.category)}
            />
          ))}
        </div>

         {/* <AsideContent alerts={alerts} filters={filters} setFilters={setFilters} /> */}
      </aside>

      <Offcanvas show={showAlerts} onHide={handleClose} placement="end">
        <Offcanvas.Header closeButton>
          <Offcanvas.Title>Alertas</Offcanvas.Title>
        </Offcanvas.Header>
        <Offcanvas.Body>
          <AsideContent alerts={alerts} filters={filters} setFilters={setFilters} />
        </Offcanvas.Body>
      </Offcanvas>

      <div className="w-100 p-4" style={{ overflowY: "auto" }}>
        <h1 className="mb-4" style={{ fontSize: "2rem", fontWeight: "600" }}>
          Dashboard
        </h1>
        <section
          className="mb-5 p-4 rounded shadow flex-column"
          style={{ backgroundColor: "#fff" }}
        >
          <h2
            className="mb-4"
            style={{ fontSize: "1.5rem", fontWeight: "600" }}
          >
            Gráficos
          </h2>

          <div className="d-flex flex-wrap gap-4 justify-content-around">
            <div
              className="d-flex flex-column align-items-center p-3 rounded border"
              style={{
                width: "320px",
                backgroundColor: "#f9f9f9",
                boxShadow: "0 1px 5px rgba(0,0,0,0.1)",
              }}
              role="button"
              onClick={() => navigate(ROUTES.PREOPERATIVE)}
            >
              <h4 className="mb-3 text-center" style={{ fontSize: "1.25rem" }}>
                Revisión diaria
              </h4>
              <div style={{ width: "220px", height: "220px" }}>
                <DoughnutChart
                  data={{
                    labels: Object.keys(dailyReview || {}),
                    datasets: [
                      {
                        label: "Revisión diaria",
                        data: Object.values(dailyReview || {}) || [0, 0, 0],
                        backgroundColor: ["#a8f584", "#f58b84", "#f9f65b"],
                      },
                    ],
                  }}
                />
              </div>
            </div>

            <div
              className="d-flex flex-column align-items-center p-3 rounded border"
              style={{
                width: "320px",
                backgroundColor: "#f9f9f9",
                boxShadow: "0 1px 5px rgba(0,0,0,0.1)",
              }}
              role="button"
              onClick={() => navigate(ROUTES.COMPLIANCE_ANNUAL_PLAN_ACTIVITIES)}
            >
              <h4 className="mb-3 text-center" style={{ fontSize: "1.25rem" }}>
                Cumplimiento plan anual de trabajo
              </h4>
              <div style={{ width: "220px", height: "220px" }}>
                <DoughnutChart
                  data={{
                    labels: Object.keys(annualPlanCompliance || {}),
                    datasets: [
                      {
                        label: "Plan trabajo",
                        data: Object.values(annualPlanCompliance || {}) || [
                          0, 0,
                        ],
                        backgroundColor: BACKGROUND_PIE_COLORS,
                      },
                    ],
                  }}
                />
              </div>
            </div>

            <div
              className="d-flex flex-column align-items-center p-3 rounded border"
              style={{
                width: "320px",
                backgroundColor: "#f9f9f9",
                boxShadow: "0 1px 5px rgba(0,0,0,0.1)",
              }}
              role="button"
              onClick={() => navigate(ROUTES.COMPLIANCE_ANNUAL_PLAN_ACTIVITIES)}
            >
              <h4 className="mb-3 text-center" style={{ fontSize: "1.25rem" }}>
                Cumplimiento plan anual de capacitación
              </h4>
              <div style={{ width: "220px", height: "220px" }}>
                <DoughnutChart
                  data={{
                    labels: Object.keys(trainingPlanCompliance || {}),
                    datasets: [
                      {
                        label: "Plan capacitación",
                        data: Object.values(trainingPlanCompliance || {}) || [
                          0, 0,
                        ],
                        backgroundColor: BACKGROUND_PIE_COLORS,
                      },
                    ],
                  }}
                />
              </div>
            </div>
          </div>
        </section>
        <section
          className="mb-5 p-4 rounded shadow flex-column"
          style={{ backgroundColor: "#fff" }}
        >
          <h2
            className="mb-4"
            style={{ fontSize: "1.5rem", fontWeight: "600" }}
          >
            Indicadores
          </h2>
          <div className="d-flex flex-wrap gap-4 justify-content-around">
            <div
              className="d-flex flex-column align-items-center p-3 rounded border"
              style={{
                width: "320px",
                backgroundColor: "#f9f9f9",
                boxShadow: "0 1px 5px rgba(0,0,0,0.1)",
              }}
              role="button"
              onClick={() => navigate(ROUTES.PREOPERATIVE)}
            >
              <h4 className="mb-3 text-center" style={{ fontSize: "1.25rem" }}>
                Vehículos que operan diariamente
              </h4>
              <div
                className="d-flex align-items-center justify-content-center"
                style={{ width: "200px", height: "200px" }}
              >
                <CardNumber number={countDailyOperatingVehicles} />
              </div>
            </div>

            <div
              className="d-flex flex-column align-items-center p-3 rounded border"
              style={{
                width: "320px",
                backgroundColor: "#f9f9f9",
                boxShadow: "0 1px 5px rgba(0,0,0,0.1)",
              }}
              role="button"
              onClick={() => navigate(ROUTES.LIST_MAINTENANCE)}
            >
              <h4 className="mb-3 text-center" style={{ fontSize: "1.25rem" }}>
                Cumplimiento de los mantenimientos
              </h4>
              <p className="text-muted mb-2" style={{ fontSize: "0.8rem" }}>
                <strong>Periodo:</strong> {DateUtil.format(maintenanceCountFilters.startDate)} - {DateUtil.format(maintenanceCountFilters.endDate)}
              </p>
              <div
                className="d-flex align-items-center justify-content-center"
                style={{ width: "200px", height: "200px" }}
              >
                <CardNumber
                  number={calculateCompliancePercentage(
                    countMaintenances.countPreventiveMaintenancePerformed,
                    countMaintenances.countSchedulePreventiveMaintenance
                  )}
                />
              </div>
            </div>

            <div
              className="d-flex flex-column align-items-center p-3 rounded border"
              style={{
                width: "320px",
                backgroundColor: "#f9f9f9",
                boxShadow: "0 1px 5px rgba(0,0,0,0.1)",
              }}
              role="button"
              onClick={() => navigate(ROUTES.LIST_ACCIDENTS)}
            >
              <h4 className="mb-3 text-center" style={{ fontSize: "1.25rem" }}>
                Días sin siniestros
              </h4>
              <div
                className="d-flex align-items-center justify-content-center"
                style={{ width: "200px", height: "200px" }}
              >
                <CardNumber number={daysWithoutIncidents} />
              </div>
            </div>
          </div>
        </section>
      </div>
    </main>
  );
}

export default Dashboard;
